import { Link } from "@tanstack/react-router";

import { PolicyClause, PolicyDocument, PolicySection } from "../components/PolicyDocument.js";
import { legalParties, TERMS_LAST_UPDATED } from "../lib/legal.js";
import { useTenant } from "../lib/tenant.js";

/**
 * The Terms of Service, ported from boxed.gg's and read against this storefront's legal
 * identity, brand, site and currency. The words that name a party, an address or a forum come
 * from the resolved config; the rest is the document. Sections boxed has for things this
 * storefront does not offer (subscriptions, crypto deposits, referral rewards, a marketplace,
 * price protection, mobile apps) are left out, and its Purchase Terms are folded into section 8,
 * so nothing here links to a page that does not exist.
 */
export function TermsPage() {
  const { config } = useTenant();
  const p = legalParties(config);
  const fairness = <Link to="/fairness">provably fair</Link>;

  return (
    <PolicyDocument title="Terms of Service" lastUpdated={TERMS_LAST_UPDATED}>
      <p>
        These Terms of Service (the “<strong>Terms</strong>”) are a legal contract between{" "}
        <strong>{p.company}</strong> and its affiliates (collectively, “<strong>Company</strong>”, “
        <strong>we</strong>” or “<strong>us</strong>”) and “<strong>you</strong>” (“
        <strong>your</strong>,” or “<strong>User</strong>”). The Terms explain how you are permitted
        to use the services provided by and through our platform, website(s) and our associated
        internet properties (either linked by Company and/or by affiliated companies) (all of these
        virtual properties, collectively, the “<strong>Site</strong>”). These Terms also govern your
        use of all the text, data, information, software, analytics, graphics, proprietary content
        and more (all of which we refer to as “<strong>Materials</strong>”) that we and/or our
        affiliates may make available to you, as well as any services we may provide through the
        Site. Collectively, the Site, the Materials, and the services provided by the Company are
        referred herein to as the <strong>“Service”</strong>.
      </p>
      <p>
        <strong>IMPORTANT</strong> – <strong>PLEASE READ CAREFULLY</strong>. BY AGREEING TO THESE
        TERMS, OR BY ACCESSING, REGISTERING IN, OPENING AN ACCOUNT, MAKING PURCHASES, PROVIDING
        INFORMATION THROUGH, OR GENERALLY USING THE SERVICE, YOU INDICATE THAT YOU HAVE BOTH READ
        AND ACCEPT THESE TERMS. IF YOU DO NOT AGREE WITH ANY OF THESE TERMS, DO NOT ACCESS OR
        OTHERWISE USE THE SERVICE OR ANY MATERIALS OR CONTENT CONTAINED THEREIN OR PROVIDE US WITH
        ANY INFORMATION ABOUT YOU.
      </p>
      <p>
        <strong>
          These Terms contain a dispute resolution and{" "}
          <a href="#arbitration">arbitration provision</a> (See Section 18), including a{" "}
          <a href="#arbitration">class action waiver</a> that affects your rights. These provisions
          apply solely to the extent applicable in your jurisdiction.
        </strong>
      </p>

      <PolicySection id="modifications" number={1} title="MODIFICATIONS AND ADDITIONAL TERMS.">
        <PolicyClause label="(a)" title="Changes to these Terms">
          Company can change, update, add or remove provisions of these Terms at any time by posting
          the updated Terms on the Site and by providing a notice on the Service. We will ask for
          your express consent to the updated Terms when and where we are legally required to do so.
          If you do not agree with any of the updated Terms, you must stop using the Service.
          Continued use of the Service following notice of any such modifications indicates You
          acknowledge and agree to be bound by the modifications. Unless otherwise required by law,
          the updated Terms are effective as of the day of posting.
        </PolicyClause>
        <PolicyClause label="(b)" title="Changes to the Service">
          Company may make changes to the Service at any time, without notice to you. If you object
          to any changes to the Service, your sole recourse will be to cease using the Service.
          Continued use of the Service following posting of any such changes will indicate your
          acknowledgement of such changes and satisfaction with the Service as modified. We also
          reserve the right to discontinue the Service, or any component of it, at any time without
          notice to you. We will not be liable to you or any third-party should we exercise our
          right to modify or discontinue the Service.
        </PolicyClause>
        <PolicyClause label="(c)" title="Additional Terms">
          In addition, certain features of the Service may be subject to additional legal terms of
          use (“<strong>Additional Terms</strong>”), which shall be provided to you at the moment
          you choose to use such features or services. By using such features, or any part thereof,
          you agree to be bound by the Additional Terms applicable to such features. In the event
          that any of the Additional Terms governing such features conflict with these Terms, the
          Additional Terms will govern.
        </PolicyClause>
      </PolicySection>

      <PolicySection id="privacy" number={2} title="PRIVACY.">
        <p>
          Please review our privacy policy available at{" "}
          <Link to="/privacy">{p.siteUrl}/privacy</Link> (the “<strong>Privacy Policy</strong>”)
          which explains how we use information that you submit to Company. The{" "}
          <Link to="/privacy">Privacy Policy</Link> is hereby incorporated by reference.
        </p>
      </PolicySection>

      <PolicySection id="contractual-relationship" number={3} title="CONTRACTUAL RELATIONSHIP.">
        <p>
          By using the Service, you represent that you are at least 18 years of age, or that you are
          of age under the laws of your jurisdiction, and lawfully able to enter into contracts.
          Pursuant to 47 U.S.C. Section 230(d) as amended, we hereby notify you that parental
          control protections (such as computer hardware, software or filtering services) are
          commercially available and may assist you in limiting access to material that is harmful
          to minors.
        </p>
        <p>
          Your access to and use of the Service is subject to your continued compliance with these
          Terms and all applicable laws. If you breach these Terms, your right to access and use the
          Service will terminate immediately, without any further action by Company, except for
          those obligations expressly described by the Company in these Terms.
        </p>
      </PolicySection>

      <PolicySection id="service-license" number={4} title="THE SERVICE AND LICENSE TO USE IT.">
        <p>
          The Service is a hosted storefront on which you may purchase {p.units} (defined in Section
          8), open boxes whose contents are determined by a {fairness} draw, and keep the items you
          receive in your inventory, sell them back to Company, or have physical items shipped to
          you. For as long as you agree to these Terms and abide by them, you may use the Service.
          These Terms apply to all users of the Service, including visitors and Registered Users.
          The Service is licensed, not sold, to you.
        </p>
        <p>
          The Service is protected by copyright laws throughout the world. Subject to your
          agreement, and continuing compliance with these Terms, Company grants you a personal,
          non-exclusive, non-transferable, non-sublicensable, revocable limited license subject to
          the limitations below to use the Service solely for your own individual, non-commercial
          purposes only. You agree not to use the Service for any other purpose.
        </p>
        <p>
          All rights not expressly granted to you in these Terms are reserved and retained by
          Company and/or its licensors. The licenses granted by Company terminate if you do not
          comply with these Terms and/or any other Service terms and conditions. By using the
          Service, you represent that you are not a person barred from using the Service under the
          laws, rules and regulations of the United States of America, your place of residence or
          any other applicable jurisdiction. No other rights, assignment, licenses or legal
          relationship of any nature, including, but not limited to, agency, partnership,
          joint-venture, employer-employee, franchisor-franchisee or otherwise, either express or
          implied, are created through your use of the Service unless expressly set forth in these
          Terms.
        </p>
      </PolicySection>

      <PolicySection id="registration" number={5} title="REGISTRATION AND ACCOUNTS.">
        <PolicyClause label="(a)" title="Visitors and Registered Users">
          Visitors and guests may browse the Site in accordance with these Terms, but will not have
          full access to the Service without first becoming Registered Users. In order to access
          certain features of the Service you may be required to become a Registered User. A “
          <strong>Registered User</strong>” is a user who has registered an account with us (your “
          <strong>Account</strong>”). Please refer to our <Link to="/privacy">Privacy Policy</Link>{" "}
          for more details about submitting information to us.
        </PolicyClause>
        <PolicyClause label="(b)" title="Registration Data and Sign-in">
          In registering for the Service, you agree to (i) provide true, accurate, current, and
          complete information about yourself as prompted by the Service (the “
          <strong>Registration Data</strong>”); and (ii) maintain and promptly update the
          Registration Data to keep it true, accurate, current and complete. If you provide any
          information that is untrue, inaccurate, not current or incomplete, or Company has
          reasonable grounds to suspect that such information is untrue, inaccurate, not current or
          incomplete, Company has the right to suspend or terminate your Account and refuse any and
          all current or future use of the Service (or any portion thereof). You sign in to your
          Account with a link we send to your email address, so anyone with access to that email
          account can access your Account: you are responsible for keeping it secure, for all
          activities that occur under your Account, and you agree to notify Company immediately of
          any unauthorized use of your Account or any other breach of security at{" "}
          <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>. Even if you notify us, you will
          be responsible for any activities that occur using your Account, including any charges
          resulting from its use. Company will not be liable for any losses caused by any
          unauthorized use of your Account.
        </PolicyClause>
        <PolicyClause label="(c)" title="Account Management">
          You agree not to create an Account using a false identity or information.{" "}
          <strong>
            You agree that you shall not create, use, or have more than one Account in the Service.
          </strong>{" "}
          Company shall be entitled to monitor your username and, at its discretion, require you to
          change it. Company reserves the right to remove or reclaim any usernames at any time and
          for any reason, including but not limited to, claims by a third party that a username
          violates the third party’s rights. You agree not to register for an Account on behalf of
          an individual other than yourself, or register for an Account on behalf of any group or
          entity unless you are authorized to bind such person, group, organization, or entity to
          these Terms. By registering another person, group, organization, or entity you hereby
          represent that you are authorized to do so. You agree not to create an Account or use the
          Service if you have been previously removed by Company, or if you have been previously
          banned or barred from any of the Company properties.
        </PolicyClause>
        <PolicyClause label="(d)" title="Identity Verification">
          Company may require you to verify your identity, through a third-party identity
          verification provider, before you may withdraw physical items or at any other time Company
          reasonably considers it necessary to prevent fraud, comply with law or protect the
          Service. Company may decline to verify, and may suspend or terminate the Account of, a
          User who is under 18, who is located in a jurisdiction where Company does not offer the
          Service, or whose identity is already associated with another Account.
        </PolicyClause>
      </PolicySection>

      <PolicySection id="accounts" number={6} title="ACCOUNTS AND CANCELLATION.">
        <PolicyClause label="(a)" title="No Cost Accounts">
          By registering for an Account, you obtain access to the Service. The rights and privileges
          provided under your Account are personal and non-transferable. There is no cost associated
          with registering for an Account with us and using the free options of the Service. You may
          cancel your Account at any time by contacting us at{" "}
          <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>.
        </PolicyClause>
        <PolicyClause label="(b)" title="What Cancellation Means">
          Company reserves the right to refuse services for any reason and may interrupt access to
          the Service for maintenance. If you have {p.units} or items in your Account at the time
          you cancel, please see Section 8(h): items should be withdrawn or sold back first, and
          unused {p.units} are forfeited unless applicable law requires otherwise.
        </PolicyClause>
      </PolicySection>

      <PolicySection id="payments" number={7} title="PAYMENTS.">
        <PolicyClause label="(a)" title="Fees and Payment">
          If you purchase any offering within the Service for a fee (collectively “
          <strong>Fees</strong>”), you agree and consent to Company’s use of third-party payment
          providers for billing and processing online payments. You agree to pay the applicable Fees
          for the offering or Service, plus all related taxes, and to reimburse us for all
          collection costs and interest for any overdue amounts. Unless otherwise negotiated and
          confirmed in writing, the Fees for any offering within the Service will be as displayed on
          the Service at the time of purchase. Failure to pay Fees when due may result in the
          suspension or termination of your access to the Service. You agree that, if applicable, we
          may charge any unpaid fees to the payment method You provided and/or send you a bill for
          such unpaid fees.
        </PolicyClause>
        <PolicyClause label="(b)" title="Payment Method Authorization">
          When you make a purchase, you must select a payment method and authorize Company and its
          payment provider to charge it for the purchase. You agree and consent that third-party
          payment providers may impose additional terms. You must promptly update your billing and
          payment information, and you are responsible for all payment information you provide and
          any related fees or penalties.
        </PolicyClause>
        <PolicyClause label="(c)" title="Taxes and Additional Fees">
          You agree to pay any taxes resulting from your use of the Service. Company is not
          responsible for bank fees incurred due to check cards, automatic payments, insufficient
          funds, or other fees from your financial institution. If Company receives less than full
          payment due to taxes, bank charges, or transfer fees, you will be invoiced for the
          difference. You also agree to pay all attorney and collection fees arising from efforts to
          collect past-due Fees. Company may change its fees or billing methods at any time.
        </PolicyClause>
      </PolicySection>

      <PolicySection
        id="purchases"
        number={8}
        title={`PURCHASES, ${p.units.toUpperCase()}, BOXES, INVENTORY AND WITHDRAWALS.`}
      >
        <PolicyClause label="(a)" title={`${p.units}`}>
          Through the Service, you may purchase credits (“<strong>{p.units}</strong>”), which can be
          used to open boxes (which include undisclosed items) and to enter battles, all of which
          are considered “intangible assets.” {p.units} have no cash value and are for entertainment
          purposes only. {p.units} cannot be withdrawn, sold, transferred, or converted to fiat
          currency. Company may offer differing amounts of {p.units} for differing prices. All
          prices are displayed on the Service, and previous offers or discounts may be removed at
          any time. The value of purchased {p.units} does not vary after purchase. {p.units} do not
          expire and may only be redeemed within the Service. There are no dormancy or other fees
          applicable to {p.units}. {p.units} are not legal tender and cannot be reloaded, resold,
          transferred for value, redeemed for cash, or applied to any other Account, unless
          otherwise required by law. By providing a payment method, you expressly authorize us to
          charge the applicable Fees on said payment method as well as any applicable taxes, as
          generally set forth in Section 7.
        </PolicyClause>
        <PolicyClause label="(b)" title="Boxes and Reveals">
          When you open a box, the Service reveals the items drawn for you and reserves the box’s
          price from your balance. The purchase completes when you claim the reveal: the price is
          deducted from your balance and the items are added to your inventory. Until a reveal is
          claimed it remains pending, and you may not open another box or join a battle. Where you
          have turned on automatic claiming in your Account, the claim follows the reveal
          immediately. The contents of every box are determined by a {fairness} draw whose result
          you may verify; the odds shown for a box are the odds used. A reveal, once made, cannot be
          cancelled or exchanged, and the value shown for an item is Company’s valuation of that
          item at the time of the reveal, not a guarantee of its market price.
        </PolicyClause>
        <PolicyClause label="(c)" title="Inventory and Selling Back">
          Items in your inventory may be sold back to Company for the value shown for them when they
          were revealed, credited to your balance in {p.units}. A sale is final: an item sold back
          cannot be reclaimed. An item that has been submitted for withdrawal, or that has no value,
          cannot be sold back. Company does not claim or represent ownership of any items you
          acquire through the Service; until an item is delivered to you, what your inventory holds
          is Company’s obligation to deliver that item or, where it cannot, its value in {p.units}.
        </PolicyClause>
        <PolicyClause label="(d)" title="Withdrawals and Shipping">
          You may request that physical items in your inventory be shipped to you (a “
          <strong>Withdrawal</strong>”). Withdrawals ship only to the countries the Service lists,
          to the shipping address saved in your Account, and a shipping fee in {p.units}, shown
          beside each country, is deducted from your balance when the Withdrawal is placed and
          returned to your balance if Company cancels it. Delivery estimates are estimates. Company
          procures items from third-party vendors after a Withdrawal is placed; if an item cannot be
          sourced, Company may return its value to your balance in {p.units} instead. Company may
          require identity verification (Section 5(d)) before a Withdrawal, may hold a Withdrawal
          for review, and may limit the value withdrawn in a day. Once an item is submitted for
          Withdrawal it can no longer be sold back. You are responsible for any duties, import taxes
          or fees due on delivery.
        </PolicyClause>
        <PolicyClause label="(e)" title="Battles">
          Where the Service offers battles, the entry price of a battle is deducted from your
          balance when you create or join it, and is not refunded once the battle has started. The
          outcome of every round is determined by the same {fairness} draw as a box. When a battle
          completes, the items won by your seat, or any prize the Service hands to a non-winning
          seat, are claimed into your inventory like a box reveal.
        </PolicyClause>
        <PolicyClause label="(f)" title="Codes and Credits">
          Promotional codes, gift codes, and other complimentary credits are provided at Company’s
          discretion and may be modified, voided, or reversed at any time, including after
          redemption, to correct errors in value, eligibility, or issuance. Items obtained using
          such credits may also be reversed in cases of clear administrative or technical error.
        </PolicyClause>
        <PolicyClause label="(g)" title="All Sales Final; Refunds and Chargebacks">
          <strong>
            ALL PURCHASES OF {p.units.toUpperCase()} AND INTANGIBLE ASSETS ARE FINAL AND
            NON-REFUNDABLE.
          </strong>{" "}
          By purchasing, you agree that you will not be eligible for a refund except as required by
          law or in the case of a technical error acknowledged by the Company. Refunds will never be
          granted where a chargeback has been initiated. You acknowledge and agree that by
          completing your purchase, you waive any right of withdrawal or ‘cooling-off’ period under
          applicable law, as the digital goods are delivered immediately and consumed upon purchase.
          Digital goods, including {p.units}, are considered delivered upon successful payment and
          credit to your Account; we maintain timestamped logs of this delivery, and you agree that
          credit to your Account constitutes fulfillment of our obligation. You are responsible for
          any and all purchases made through your Account, whether or not authorized by you, and we
          do not offer refunds or accept chargebacks resulting from unauthorized access, including
          by minors, household members, or third parties. You agree to contact us first to resolve
          any issue before initiating a chargeback. If you initiate a chargeback for a valid and
          fulfilled digital purchase, we may suspend or terminate your Account, revoke any {p.units}
          , items or pending Withdrawals held in your Account, and take legal action or recover
          costs; we will provide proof of purchase, delivery, and policy agreement to payment
          processors in any chargeback dispute. We take credit card fraud very seriously: discovery
          that you have used a stolen or fraudulent credit card will result in the notification of
          the appropriate law enforcement agencies and termination of your Account. We prohibit and
          seek to prevent money laundering and the funding of criminal activities, may review
          transactions that meet certain thresholds, and may provide any evidence of such activities
          to financial regulators or law enforcement. We may prevent you from creating an Account,
          suspend or terminate your Account, or request additional information from you if (1) you
          provide fraudulent Account or payment information, such as impersonating a third party,
          (2) one or more of your transactions is flagged, or (3) you are on OFAC’s list of
          Specially Designated Nationals and Blocked Persons or a similar sanctions or terrorism
          watch list. If you refuse or fail to provide requested information in a timely manner, we
          may terminate your Account.
        </PolicyClause>
        <PolicyClause label="(h)" title="Account Closure">
          Upon closure of your Account, for any reason, any unused {p.units} are forfeited unless
          otherwise required by law. Please withdraw or sell back the items in your inventory before
          asking us to close your Account; if you have an open Withdrawal or a pending refund,
          contact us first.
        </PolicyClause>
        <PolicyClause label="(i)" title="Nature of the Service">
          The Service is not a banking institution (a place of storage, accumulation and savings of
          your deposits, or a money transfer service). The Service is not a gambling service, in the
          terminology established by law; we do not take or place illegal bets, and we do not
          recommend or encourage illegal gambling. You agree not to use the Service if doing so
          would violate the laws of your state, province, or country. It is your sole and absolute
          responsibility to comply with all applicable laws, and you assume all risk in using the
          Service. Nothing published on the Service shall be construed as legal advice on any issue.
          You understand and agree that the Service is for entertainment purposes only. We make no
          guarantee that the Service is legal in your jurisdiction.
        </PolicyClause>
      </PolicySection>

      <PolicySection id="third-party-sites" number={9} title="THIRD PARTY SITES.">
        <p>
          Our Service may contain links to third-party websites (“
          <strong>Third-Party Sites</strong>”) that are not owned or controlled by us. Your use of
          Third-Party Sites is subject to their own terms and privacy policies. WE DO NOT ENDORSE,
          VERIFY, OR TAKE RESPONSIBILITY FOR ANY CONTENT, PRODUCTS, SERVICES, OR PRACTICES OF
          THIRD-PARTY SITES, AND WE ARE NOT LIABLE FOR ANY DAMAGES OR LOSSES RELATED TO YOUR USE OF
          OR RELIANCE ON THEM. Any reference to third-party products or services on our Service does
          not imply our endorsement.
        </p>
      </PolicySection>

      <PolicySection
        id="intellectual-property"
        number={10}
        title="OUR INTELLECTUAL PROPERTY RIGHTS."
      >
        <p>
          The Service and all content, features, and functionality—including text, graphics,
          software, images, audio, video, data, and design—are the exclusive property of the Company
          or its licensors and are protected by intellectual property laws (USA and
          internationally). You receive no rights to the Service or its Materials except as
          expressly granted in these Terms. All rights not expressly granted are reserved. Any
          unauthorized use may violate these Terms and applicable laws. “Materials” include all
          information and content on the Service, including its look and feel. While we strive for
          accuracy, <strong>your use of the Service is at your own risk.</strong>
        </p>
        <p>
          We do not claim or represent ownership of any items you may acquire or purchase through
          the Service.
        </p>
        <p>
          Any comments, suggestions, or materials you provide to us about the Service (“
          <strong>Feedback</strong>”) are considered non-confidential and non-proprietary. You
          assign all rights in your Feedback to us, and we may use it for any purpose without
          attribution or compensation. If assignment is not allowed by law, you grant us an
          exclusive, worldwide, royalty-free license to use your Feedback. We are not required to
          use or display any Feedback you provide.
        </p>
      </PolicySection>

      <PolicySection id="acceptable-use" number={11} title="ACCEPTABLE USE POLICY.">
        <p>
          You must not use the Service in any way that violates laws or these Terms. Prohibited
          actions include, but are not limited to:
        </p>
        <ul>
          <li>Breaking any law or regulation</li>
          <li>Harassing, stalking, or harming others</li>
          <li>Impersonating others or misrepresenting your identity</li>
          <li>
            Copying, distributing, or exploiting Service content or Materials without permission
          </li>
          <li>Circumventing security or access controls</li>
          <li>
            Removing or bypassing copyright, trademark, or other content protection measures on the
            Service.
          </li>
          <li>
            Using automated systems (e.g., bots, spiders) to access the Service or imposing (as
            determined in our sole discretion) an unreasonable or disproportionately large load on
            our infrastructure
          </li>
          <li>Introducing malware or interfering with Service operations</li>
          <li>
            Mirroring, framing, scraping, deep linking, or accessing Service content by unauthorized
            means
          </li>
          <li>
            Collecting personal information or using the Service for commercial solicitation without
            consent
          </li>
          <li>
            Reverse engineering, attempt to access the source code, or creating derivative works
            from the Service
          </li>
          <li>Posting unlawful, offensive, or harmful content</li>
          <li>Infringing on the intellectual property or other rights of a third party</li>
          <li>
            Using the Service to violate anyone’s legal rights, promote illegal activity, or expose
            anyone to legal liability
          </li>
          <li>
            Engaging in spam, scams, unauthorized promotions, unlawful contests, lotteries, gambling
            or pyramid schemes
          </li>
          <li>Creating multiple accounts to bypass restrictions</li>
          <li>Harming or exploiting children</li>
          <li>Any other objectionable conduct as determined by Company</li>
        </ul>
        <p>
          You are responsible for any violations of these Terms and must cover our legal costs and
          damages if your actions cause us problems. We may take over the defense of any claim and
          you agree to cooperate.
        </p>
        <p>
          <strong>We reserve the right, but do not assume the obligation, to investigate</strong>{" "}
          and act on <strong>any violation of these Terms or misuse of the Service.</strong> We may
          monitor all prohibited actions, investigate, and/or take appropriate action at{" "}
          <u>our sole discretion</u> against you, including suspending or terminating your Account,
          taking legal action, or reporting you to authorities. WE COOPERATE WITH LAW ENFORCEMENT
          and may share your information as needed to protect our rights, enforce these Terms, or
          comply with our <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection id="user-submissions" number={12} title="USER SUBMISSIONS AND INTERACTIONS.">
        <PolicyClause label="(a)" title="User Submissions">
          If we allow user-generated content, you may post usernames, comments, or other materials
          (“<strong>User Submissions</strong>”), some of which may be public: your username and your
          activity on the Service, such as the boxes you open and the battles you play, may be shown
          to other users. You must own or have permission to post your content, and it must not
          violate anyone’s rights or any laws.
        </PolicyClause>
        <p>
          You retain ownership of your User Submissions, but by posting them, you grant us and
          others a worldwide, royalty-free license to use, modify, display, and distribute your
          content for any purpose, without notice or compensation. You waive any moral rights to
          your content to the extent allowed by law. You are responsible for any fees or royalties
          owed to others for your User Submissions.
        </p>
        <p>
          We may monitor, edit, or remove User Submissions at our discretion and are not responsible
          for their content. User Submissions do not reflect our views, and you are solely
          responsible for what you post. We are not liable for any content posted by you or others,
          or for any consequences of your postings. We may delete User Submissions after the Service
          ends.
        </p>
        <p>
          You may encounter content you find objectionable or inappropriate. We are not responsible
          for your exposure to such content.
        </p>
        <p>Be cautious about sharing personal information publicly on the Service.</p>
        <PolicyClause label="(b)" title="Users Interactions">
          You are responsible for your interactions with other users and assume all associated
          risks. We are not liable for any disputes or issues arising from user interactions or User
          Submissions. We may limit your connections or restrict your use of the Service as needed
          and may monitor or intervene in disputes at our discretion. You agree to cooperate with
          any investigations into improper activity.
        </PolicyClause>
        <p>
          If you have a dispute with another user, you release us from any related claims or
          damages. If you are a California resident, you waive California Civil Code § 1542, which
          says: “A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR
          SUSPECT TO EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH IF KNOWN
          BY HIM OR HER MUST HAVE MATERIALLY AFFECTED HIS SETTLEMENT WITH THE DEBTOR.”
        </p>
      </PolicySection>

      <PolicySection id="infringement" number={13} title="INTELLECTUAL PROPERTY INFRINGEMENT.">
        <p>
          We respect the intellectual property rights of others and encourage You to do the same.
          Accordingly, We have a policy of removing any content that violates intellectual property
          rights of others, suspending access to this Service (or any portion thereof) to any user
          who uses this Service in violation of someone’s intellectual property rights, and/or
          terminating in appropriate circumstances the account of any user who uses the this Service
          in violation of someone’s intellectual property rights.
        </p>
        <p>
          Pursuant to Title 17 of the United States Code, Section 512, We have implemented
          procedures for receiving written notification of claimed copyright infringement and for
          processing such claims in accordance with such law. If You believe Your copyright or other
          intellectual property right is being infringed by a user of this Service, please provide
          written notice to Our Agent for notice of claims of infringement at DMCA Agent; Email:{" "}
          <a href={`mailto:${p.legalEmail}`}>{p.legalEmail}</a>.
        </p>
        <p>To be sure the matter is handled immediately, Your written notice must:</p>
        <ul>
          <li>Contain Your physical or electronic signature;</li>
          <li>
            Identify the copyrighted work or other intellectual property alleged to have been
            infringed;
          </li>
          <li>
            Identify the allegedly infringing material in a sufficiently precise manner to allow Us
            to locate that material;
          </li>
          <li>
            Contain adequate information by which We can contact You (including postal address,
            telephone number, and e-mail address);
          </li>
          <li>
            Contain a statement that You have a good faith belief that use of the copyrighted
            material or other intellectual property is not authorized by the owner, the owner's
            agent or the law;
          </li>
          <li>Contain a statement that the information in the written notice is accurate; and</li>
          <li>
            Contain statement, under penalty of perjury, that You are authorized to act on behalf of
            the copyright or other intellectual property right owner.
          </li>
        </ul>
        <p>
          Unless the notice pertains to copyright or other intellectual property infringement, the
          Agent will be unable to address the listed concern.
        </p>
        <h3>Submitting a DMCA Counter-Notification</h3>
        <p>
          If the content removed belongs to you, We will notify You that We have removed or disabled
          access to copyright-protected material that You provided, if such removal is pursuant to a
          validly received DMCA take-down notice. In response, You may provide Our Agent with a
          written counter-notification that includes the following information:
        </p>
        <ol>
          <li>Your physical or electronic signature;</li>
          <li>
            Identification of the material that has been removed or to which access has been
            disabled, and the location at which the material appeared before it was removed or
            access to it was disabled;
          </li>
          <li>
            A statement from You under the penalty of perjury, that You have a good faith belief
            that the material was removed or disabled as a result of a mistake or misidentification
            of the material to be removed or disabled; and
          </li>
          <li>
            Your name, physical address and telephone number, and a statement that You consent to
            the jurisdiction of a court for the judicial district in which Your physical address is
            located, or if Your physical address is outside of the United States, for any judicial
            district in which We may be located, and that You will accept service of process from
            the person who provided notification of allegedly infringing material or an agent of
            such person.
          </li>
        </ol>
        <h3>Termination of Repeat Infringers</h3>
        <p>
          We reserve the right, in Our sole discretion, to terminate the account or access of any
          user of this Service who is the subject of repeated DMCA or other infringement
          notifications.
        </p>
      </PolicySection>

      <PolicySection id="termination" number={14} title="TERM AND TERMINATION.">
        <p>
          These Terms take effect when you begin using the Service and remain in force until you or
          we terminate your access. You may terminate your use at any time by notifying us, closing
          your Account, or simply stopping use of the Service. To close your Account, contact
          support at <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>.
        </p>
        <p>
          We may suspend or terminate your account or access to the Service at any time, for any
          reason, including violations of these Terms. Upon termination, all licenses end, your
          account and related content (including User Submissions) may be deleted, and you must
          destroy any downloaded materials. We are not liable for any loss resulting from suspension
          or termination. Certain provisions, such as ownership, disclaimers, and limitations of
          liability, will survive termination.
        </p>
        <p>
          If you have items in your Account, termination will not be effective until you dispose of
          them as described in Section 8(h).
        </p>
      </PolicySection>

      <PolicySection id="warranties" number={15} title="DISCLAIMER OF WARRANTIES.">
        <p>
          You use the Service at your own risk. THE SERVICE IS PROVIDED ON AN “AS IS” AND “AS
          AVAILABLE” BASIS, WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT
          PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT,
          SATISFACTORY QUALITY, AND THOSE ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. COMPANY
          DOES NOT WARRANT THAT YOU WILL BE ABLE TO ACCESS OR USE THE SERVICE; THAT THE SERVICE WILL
          BE UNINTERRUPTED OR ERROR-FREE; OR THAT ALL DEFECTS WILL BE CORRECTED; OR THAT THE SERVICE
          IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
        </p>
        <p>
          WE MAKE NO GUARANTEES ABOUT THE ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY MATERIALS OR
          CONTENT, OR ABOUT THE RESULTS FROM USING THE SERVICE. WE MAY REMOVE CONTENT OR MATERIALS
          AT ANY TIME WITHOUT NOTICE AND ARE NOT LIABLE FOR ANY SUCH REMOVAL. WE DO NOT GUARANTEE
          CONTINUOUS OR SECURE ACCESS TO THE SERVICE.
        </p>
        <p>
          WE WILL TRY TO PROCESS WITHDRAWALS PROMPTLY, BUT MAKE NO PROMISES ABOUT PROCESSING TIMES,
          ESPECIALLY WHEN THIRD PARTIES OR EXTERNAL SYSTEMS ARE INVOLVED, SUCH AS THIRD PARTY
          VENDORS, SHIPPING CARRIERS, THE AVAILABILITY OF ITEMS, ELECTRONIC BILLING OR EXTERNAL
          PAYMENT SYSTEMS, OR ACCREDITATION OF PAYMENTS OR TRANSFERS IN ANY FORM OF ELECTRONIC MONEY
          NOT DENOMINATED IN THE SERVICE.
        </p>
      </PolicySection>

      <PolicySection id="liability" number={16} title="LIMITATION OF LIABILITY.">
        <p>
          COMPANY SHALL NOT BE LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL,
          PUNITIVE, REMOTE OR OTHER SIMILAR DAMAGES, INCLUDING, BUT NOT LIMITED TO, LOSS OF
          REVENUES, LOST PROFITS, LOST DATA, OR BUSINESS INTERRUPTION OR OTHER INTANGIBLE LOSSES
          (HOWEVER SUCH LOSSES ARE QUALIFIED), ARISING OUT OF OR RELATING IN ANY WAY TO THESE TERMS
          OR THE SERVICE ITSELF, WHETHER BASED ON CONTRACT, TORT OR ANY OTHER LEGAL THEORY, AND
          WHETHER OR NOT COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT
          SHALL COMPANY’S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE
          TERMS OR THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO COMPANY IN THE
          THREE (3) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S.
          DOLLARS (USD $100). THE LIMITATIONS OF DAMAGES SET FORTH HEREIN ARE FUNDAMENTAL ELEMENTS
          OF THE BASIS OF THE BARGAIN BETWEEN COMPANY AND YOU.
        </p>
        <p>
          If applicable law does not allow us to disclaim certain warranties or limit our liability
          as stated in these Terms, our responsibility will be limited to the minimum extent
          permitted by law.{" "}
          <em>
            Nothing in these Terms affects your legal rights as a consumer or excludes or limits any
            liability that cannot be legally excluded or limited.
          </em>
        </p>
      </PolicySection>

      <PolicySection id="indemnity" number={17} title="INDEMNITY.">
        <p>
          You agree to indemnify and hold harmless Company and its affiliates, officers, employees,
          agents, partners, and licensors from any losses, costs, liabilities, or expenses
          (including reasonable attorneys’ fees) arising from: (a) your User Submissions; (b) your
          use or inability to use the Service; (c) your violation of these Terms; (d) your violation
          of others’ rights; or (e) your violation of any laws or regulations. Company may, at its
          own expense, assume exclusive defense of any matter you must indemnify, and you agree to
          cooperate. These obligations survive termination of your Account or these Terms.
        </p>
      </PolicySection>

      <PolicySection
        id="arbitration"
        number={18}
        title="DISPUTE RESOLUTION AND ARBITRATION; CLASS ACTION WAIVER."
      >
        <p>
          <strong>
            <em>
              Please read this carefully. If you are a resident of the United States of America,
              this affects Your rights. Otherwise, this Section applies to the extent applicable in
              your jurisdiction.
            </em>
          </strong>
        </p>
        <PolicyClause label="(a)" title="Applicable Law">
          These Terms will be subject to and construed in accordance with the laws of{" "}
          {p.governingLaw}, excluding its rules regarding conflicts of law. You agree that any claim
          or dispute You may have against Company must be resolved exclusively by a court located in{" "}
          {p.governingLaw}, except as prohibited by law, as otherwise agreed by the parties or as
          described in the Arbitration subsection below. You agree to submit to the personal
          jurisdiction of the courts located in {p.governingLaw} for the purpose of litigating all
          such claims or disputes.
        </PolicyClause>
        <PolicyClause label="(b)" title="Dispute Resolution">
          Before either party may seek arbitration as provided below, the party must first send to
          the other party a written notice of dispute (“Notice") describing the nature and basis of
          the claim or dispute, and the requested relief. After the Notice is received, You and
          Company may attempt to resolve the claim or dispute informally. If You and Company do not
          resolve the claim or dispute within thirty (30) days after the Notice is received, either
          party may begin an arbitration proceeding as provided below.
        </PolicyClause>
        <PolicyClause label="(c)" title="Arbitration">
          You agree that, to the extent that the Law permits, Company may elect to resolve the
          dispute in a cost-effective manner through binding arbitration (including
          non-appearance-based arbitration), except that you may assert claims in small claims court
          if your claims qualify. You have the right to opt-out of this provision, which means you
          would retain your right to litigate your disputes in a court, either before a judge or, if
          applicable, a jury. You must opt-out of these arbitration procedures within 30 days from
          the date that you first consent to these Terms (the “<strong>Opt-Out Deadline</strong>
          ”). You may opt out of this provision by mailing written notification to {p.postalAddress}
          . Your written notification must include (1) your name, (2) your address, and (3) a clear
          statement that you do not wish to resolve disputes with Company through arbitration. Your
          decision to opt-out of this Arbitration provision will have no adverse effect on your
          relationship with Company. Any opt-out request received after the Opt-Out Deadline will
          not be valid and you must pursue your dispute in arbitration or small claims court. In the
          event Company elects arbitration, You hereby agree to move any claims to the exclusive
          jurisdiction of an arbitration procedure, which shall be initiated through the American
          Arbitration Association or another established alternative dispute resolution provider
          (collectively, “<strong>ADR</strong>”) chosen by Company. The ADR provider and the parties
          must comply with the following rules: (i) the arbitration shall be conducted by telephone,
          online and/or be solely based on written submissions, the specific manner shall be chosen
          by the party initiating the arbitration; (ii) the arbitration shall not involve any
          personal appearance by the parties or witnesses unless otherwise mutually agreed by the
          parties, in which case the location of the arbitration shall be {p.arbitrationVenue}, and
          (iii) the award rendered by the arbitrator may be entered in any court of competent
          jurisdiction. Arbitration expressly excludes claims for injunctive or other equitable
          relief. The U.S. Federal Arbitration Act and U.S. federal arbitration law apply to these
          Terms.
        </PolicyClause>
        <PolicyClause label="(d)" title="Waiver of Jury Trial">
          EXCEPT AS PROHIBITED BY LAW, YOU HEREBY WAIVE YOUR CONSTITUTIONAL AND STATUTORY RIGHTS TO
          GO TO COURT AND HAVE A TRIAL IN FRONT OF A JUDGE OR A JURY. In the event any litigation
          should arise between You and Company in any state or federal court in a suit to vacate or
          enforce an arbitration award or otherwise, YOU WAIVE ALL RIGHTS TO A JURY TRIAL, instead
          electing that the dispute be resolved by a judge.
        </PolicyClause>
        <PolicyClause label="(e)" title="Waiver of Class or Consolidated Actions">
          EXCEPT AS PROHIBITED BY LAW, ALL CLAIMS AND DISPUTES WITHIN THE SCOPE OF THESE TERMS MUST
          BE ARBITRATED OR LITIGATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS BASIS, AND CLAIMS OF
          MORE THAN ONE USER CANNOT BE ARBITRATED OR LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE OF
          ANY OTHER USER. TO THE EXTENT THAT THE LAW PERMITS, EACH PARTY WAIVES THE RIGHT TO
          LITIGATE IN COURT OR ARBITRATE ANY CLAIM OR DISPUTE AS A CLASS ACTION, EITHER AS A MEMBER
          OF A CLASS OR AS A REPRESENTATIVE.
        </PolicyClause>
      </PolicySection>

      <PolicySection
        id="electronic-communications"
        number={19}
        title="ELECTRONIC COMMUNICATIONS; AI CHATBOT."
      >
        <p>
          When you interact with the Company — such as by visiting the website, sending emails, or
          using the Service — you agree to communicate electronically. This includes receiving
          emails from the Company or seeing notices posted on the website. By doing this, you (1)
          agree to get communications from the Company electronically, and (2) accept that all
          agreements, notices, and other messages sent electronically have the same legal effect as
          if they were in writing. This does not affect any legal rights you have by law. If the
          Company asks for your email address, you must give them your current and correct email. If
          the last email address you gave is not valid or cannot receive messages, the Company’s
          attempt to send you an email will still count as proper notice. The Company is not
          responsible if you do not receive emails because of filters or blocks set up by you, your
          network, or your email provider.
        </p>
        <p>
          We may use artificial intelligence-driven chat interfaces (“<strong>AI Chatbots</strong>”)
          to respond to your inquiries. These AI Chatbot communications are not legally binding on
          Company or its affiliates and may not reflect Company’s current policies or positions. For
          official or legally binding information, please refer to our official documentation or
          contact Company directly. By using the AI Chatbot, you acknowledge that Company is not
          responsible for any errors or actions taken based on chatbot responses. For official
          assistance or legally binding matters, please contact us directly as set forth in Section
          22 below.
        </p>
      </PolicySection>

      <PolicySection id="consumer-notice" number={20} title="CONSUMER NOTICE.">
        <p>
          Under California Civil Code Section 1789.3, California users are entitled to the following
          consumer rights notice: The Service is provided by {p.legalName}. If you have a question
          or complaint regarding the Service, please contact Company’s Customer Service at{" "}
          <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>; Attention: Customer Service.
          California residents may reach the Complaint Assistance Unit of the Division of Consumer
          Services of the California Department of Consumer Affairs by post at 1625 North Market
          Blvd., Sacramento, CA 95834 or by telephone at (916) 445-1254 or (800) 952-5210 or Hearing
          Impaired at TDD (800) 326-2297 or TDD (916) 322-1700.
        </p>
      </PolicySection>

      <PolicySection id="general" number={21} title="GENERAL.">
        <p>
          These Terms together with our <Link to="/privacy">Privacy Policy</Link>, and any
          Additional Terms that we may make available from time to time through our internet
          properties, constitute the entire agreement between you and Company regarding your use of
          our Service and supersede and replace any prior written or oral agreements regarding the
          foregoing. Our failure to exercise or enforce any right or provision in these Terms shall
          not operate as a waiver of such right or provision. If any provision of the Terms is found
          to be unlawful, void or for any reason unenforceable, then that provision shall be deemed
          severable from the Terms and shall not affect the validity and enforceability of any
          remaining provisions. Neither the rights nor obligations arising under these Terms are
          assignable by you. Any such attempted assignment or transfer shall be void and without
          effect. We may assign these Terms without restriction.
        </p>
      </PolicySection>

      <PolicySection id="contact" number={22} title="CONTACT US.">
        <p>
          If you have any questions about these Terms or otherwise need to contact Company for any
          reason, you can reach us at <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>.
        </p>
      </PolicySection>

      <p className="mt-8 text-xs text-muted-foreground">
        ©{p.year} {p.company}. All rights reserved.
      </p>
    </PolicyDocument>
  );
}
